import React from 'react';

const MARKDOWN_LINK_PATTERN = /\[([^\]]+)\]\(([^)]+)\)/g;
const BARE_URL_PATTERN = /(https?:\/\/[^\s<]+[^\s<.,;:!?)])/g;

function isSafeLinkUrl(url: string): boolean {
  try {
    const parsed = new URL(url.trim());
    return parsed.protocol === 'https:' || parsed.protocol === 'http:';
  } catch {
    return false;
  }
}

type ContentPart =
  | { type: 'text'; value: string }
  | { type: 'link'; label: string; href: string };

function matchPattern(
  text: string,
  pattern: RegExp
): RegExpExecArray[] {
  const flags = pattern.flags.includes('g') ? pattern.flags : `${pattern.flags}g`;
  const regex = new RegExp(pattern.source, flags);
  const matches: RegExpExecArray[] = [];
  let match: RegExpExecArray | null = regex.exec(text);

  while (match !== null) {
    matches.push(match);
    match = regex.exec(text);
  }

  return matches;
}

function splitMarkdownLinks(text: string): ContentPart[] {
  const parts: ContentPart[] = [];
  let lastIndex = 0;
  const matches = matchPattern(text, MARKDOWN_LINK_PATTERN);

  for (let i = 0; i < matches.length; i += 1) {
    const match = matches[i];
    if (!match) continue;

    const matchIndex = match.index ?? 0;
    if (matchIndex > lastIndex) {
      parts.push({ type: 'text', value: text.slice(lastIndex, matchIndex) });
    }

    const label = match[1]?.trim() ?? '';
    const href = match[2]?.trim() ?? '';
    if (label && href && isSafeLinkUrl(href)) {
      parts.push({ type: 'link', label, href });
    } else {
      parts.push({ type: 'text', value: match[0] ?? '' });
    }

    lastIndex = matchIndex + (match[0]?.length ?? 0);
  }

  if (lastIndex < text.length) {
    parts.push({ type: 'text', value: text.slice(lastIndex) });
  }

  return parts;
}

function linkifyBareUrls(text: string, keyPrefix: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;
  let linkIndex = 0;
  const matches = matchPattern(text, BARE_URL_PATTERN);

  for (let i = 0; i < matches.length; i += 1) {
    const match = matches[i];
    if (!match) continue;

    const matchIndex = match.index ?? 0;
    if (matchIndex > lastIndex) {
      nodes.push(text.slice(lastIndex, matchIndex));
    }

    const href = match[0] ?? '';
    if (isSafeLinkUrl(href)) {
      nodes.push(
        <a
          key={`${keyPrefix}-url-${linkIndex}`}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
        >
          {href}
        </a>
      );
      linkIndex += 1;
    } else {
      nodes.push(href);
    }

    lastIndex = matchIndex + href.length;
  }

  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }

  return nodes.length > 0 ? nodes : [text];
}

export function renderChatMessageContent(content: string): React.ReactNode {
  const segments = splitMarkdownLinks(content);

  return segments.map((segment, index) => {
    if (segment.type === 'link') {
      return (
        <a
          key={`link-${index}`}
          href={segment.href}
          target="_blank"
          rel="noopener noreferrer"
        >
          {segment.label}
        </a>
      );
    }

    return (
      <React.Fragment key={`text-${index}`}>
        {linkifyBareUrls(segment.value, `seg-${index}`)}
      </React.Fragment>
    );
  });
}
